import { Fade } from '@mui/material';
import { INTEGRATION_PROVIDERS, type IntegrationProviders } from '@vy/types';
import { validateEnum, withDefault } from '@vy/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useTabParam } from '../../../hooks/use-tab-param';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import type { PageTab } from '../../../utils/types/page-tab';
import { ConfigurationTabContent } from './integration-provider-page-components/ConfigurationTabContent/ConfigurationTabContent';
import {
  INTEGRATION_PROVIDER_TABS,
  type IntegrationProviderTab,
} from './integration-provider-page-types/integration-provider-page-tabs';

export type IntegrationProviderTabsMap = Record<IntegrationProviderTab, PageTab<IntegrationProviderTab>>;

const IntegrationProviderPage: React.FC = () => {
  const { provider } = useValidatedParams('provider');
  validateEnum(provider, INTEGRATION_PROVIDERS);

  const [currentTab, setTab] = useTabParam<IntegrationProviderTab>(
    INTEGRATION_PROVIDER_TABS.configuration,
    deserializeIntegrationProviderTabParam,
  );
  const integrationProviderTabs = getIntegrationProviderTabs(provider);

  return (
    <>
      <Helmet>
        <title>
          {currentTab === INTEGRATION_PROVIDER_TABS.configuration ? 'Configuration' : 'Entity Mapping'}
        </title>
      </Helmet>

      <VyPageLayout
        title={`${provider} Integration Settings`}
        currentTab={withDefault(currentTab, INTEGRATION_PROVIDER_TABS.configuration)}
        tabs={[
          {
            label: 'Configuration',
            value: INTEGRATION_PROVIDER_TABS.configuration,
            onClick: () => setTab(INTEGRATION_PROVIDER_TABS.configuration),
          },
          {
            label: 'Entity Mapping',
            value: INTEGRATION_PROVIDER_TABS.entityMapping,
            onClick: () => setTab(INTEGRATION_PROVIDER_TABS.entityMapping),
          },
        ]}
      >
        {Object.entries(integrationProviderTabs).map(([tab, { id, render }]) => (
          <Fade key={id} hidden={currentTab !== tab} in={currentTab === tab}>
            <div className="h-full w-full">{currentTab === tab && render}</div>
          </Fade>
        ))}
      </VyPageLayout>
    </>
  );
};

const IntegrationProviderPageWithSuspenseBoundary = withSuspenseBoundary(
  withGlobalSearchParams(IntegrationProviderPage),
);
export { IntegrationProviderPageWithSuspenseBoundary as IntegrationProviderPage };

function deserializeIntegrationProviderTabParam(tab: string): IntegrationProviderTab | null {
  validateEnum(tab, INTEGRATION_PROVIDER_TABS);

  return tab;
}

function getIntegrationProviderTabs(provider: IntegrationProviders): IntegrationProviderTabsMap {
  return {
    [INTEGRATION_PROVIDER_TABS.configuration]: {
      id: 0,
      render: <ConfigurationTabContent provider={provider} />,
      label: 'Configuration',
      paramName: INTEGRATION_PROVIDER_TABS.configuration,
    },
    [INTEGRATION_PROVIDER_TABS.entityMapping]: {
      id: 1,
      render: <div />,
      label: 'Entity Mapping',
      paramName: INTEGRATION_PROVIDER_TABS.entityMapping,
    },
  };
}
